import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Plus, 
  AlertCircle, 
  Trash2, 
  Edit2, 
  Check,
  Shield,
  Lock,
  Calendar,
  MapPin
} from 'lucide-react';
import { paymentMethodsService } from '../../services/paymentMethodsService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Sidebar from '../../components/ui/Sidebar';

const PaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expiringCards, setExpiringCards] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user] = useState({ name: 'User', email: 'user@example.com' });

  // Form state for new/edit card
  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    cardholderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    loadPaymentMethods();
    loadExpiringCards();
  }, []);

  const loadPaymentMethods = async () => {
    setLoading(true);
    setError(null);
    
    const { data, error: fetchError } = await paymentMethodsService?.getPaymentMethods();
    
    if (fetchError) {
      setError(fetchError?.message);
    } else {
      setPaymentMethods(data || []);
    }
    
    setLoading(false);
  };

  const loadExpiringCards = async () => {
    const { data } = await paymentMethodsService?.getExpiringPaymentMethods();
    setExpiringCards(data || []);
  };

  const getCardBrandLogo = (brand) => {
    const logos = {
      visa: '💳 Visa',
      mastercard: '💳 Mastercard',
      amex: '💳 Amex',
      discover: '💳 Discover',
      diners: '💳 Diners',
      jcb: '💳 JCB',
      unionpay: '💳 UnionPay',
      unknown: '💳 Card'
    };
    return logos?.[brand] || logos?.unknown;
  };

  const detectCardType = (number) => {
    const cleaned = number?.replace(/\s/g, '');
    if (/^4/?.test(cleaned)) return 'visa';
    if (/^5[1-5]/?.test(cleaned)) return 'mastercard';
    if (/^3[47]/?.test(cleaned)) return 'amex';
    if (/^6(?:011|5)/?.test(cleaned)) return 'discover';
    return 'unknown';
  };

  const formatCardNumber = (value) => {
    const cleaned = value?.replace(/\s/g, '');
    const chunks = cleaned?.match(/.{1,4}/g) || [];
    return chunks?.join(' ');
  };

  const validateForm = () => {
    const errors = {};
    const cleanNumber = cardForm?.cardNumber?.replace(/\s/g, '');

    if (!cardForm?.cardholderName?.trim()) {
      errors.cardholderName = 'Cardholder name is required';
    }

    if (!cleanNumber || cleanNumber?.length < 13) {
      errors.cardNumber = 'Valid card number is required';
    }

    if (!cardForm?.expiryMonth || cardForm?.expiryMonth < 1 || cardForm?.expiryMonth > 12) {
      errors.expiryMonth = 'Valid month (1-12) is required';
    }

    const currentYear = new Date()?.getFullYear();
    if (!cardForm?.expiryYear || cardForm?.expiryYear < currentYear) {
      errors.expiryYear = 'Valid future year is required';
    }

    if (!cardForm?.cvv || cardForm?.cvv?.length < 3) {
      errors.cvv = 'Valid CVV is required';
    }

    if (!cardForm?.addressLine1?.trim()) {
      errors.addressLine1 = 'Address is required';
    }

    if (!cardForm?.city?.trim()) {
      errors.city = 'City is required';
    }

    if (!cardForm?.postalCode?.trim()) {
      errors.postalCode = 'Postal code is required';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    const cleanNumber = cardForm?.cardNumber?.replace(/\s/g, '');
    const cardData = {
      card_brand: detectCardType(cleanNumber),
      card_last4: cleanNumber?.slice(-4),
      card_exp_month: parseInt(cardForm?.expiryMonth),
      card_exp_year: parseInt(cardForm?.expiryYear),
      billing_name: cardForm?.cardholderName,
      billing_address_line1: cardForm?.addressLine1,
      billing_address_line2: cardForm?.addressLine2 || null,
      billing_city: cardForm?.city,
      billing_state: cardForm?.state || null,
      billing_postal_code: cardForm?.postalCode,
      billing_country: cardForm?.country,
      is_default: paymentMethods?.length === 0
    };

    if (editingCard) {
      const { error: updateError } = await paymentMethodsService?.updatePaymentMethod(
        editingCard?.id,
        cardData
      );
      
      if (updateError) {
        setError(updateError?.message);
      } else {
        setShowAddCard(false);
        setEditingCard(null);
        resetForm();
        loadPaymentMethods();
      }
    } else {
      const { error: addError } = await paymentMethodsService?.addPaymentMethod(cardData);
      
      if (addError) {
        setError(addError?.message);
      } else {
        setShowAddCard(false);
        resetForm();
        loadPaymentMethods();
      }
    }
  };

  const resetForm = () => {
    setCardForm({
      cardNumber: '',
      cardholderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    });
    setFormErrors({});
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setCardForm({
      cardNumber: `•••• •••• •••• ${card?.card_last4}`,
      cardholderName: card?.billing_name,
      expiryMonth: card?.card_exp_month?.toString(),
      expiryYear: card?.card_exp_year?.toString(),
      cvv: '•••',
      addressLine1: card?.billing_address_line1 || '',
      addressLine2: card?.billing_address_line2 || '',
      city: card?.billing_city || '',
      state: card?.billing_state || '',
      postalCode: card?.billing_postal_code || '',
      country: card?.billing_country || 'US'
    });
    setShowAddCard(true);
  };

  const handleDelete = async (cardId) => {
    const { error: deleteError } = await paymentMethodsService?.deletePaymentMethod(cardId);
    
    if (deleteError) {
      setError(deleteError?.message);
    } else {
      setDeleteConfirm(null);
      loadPaymentMethods();
    }
  };

  const handleSetDefault = async (cardId) => {
    const { error: updateError } = await paymentMethodsService?.setDefaultPaymentMethod(cardId);
    
    if (updateError) {
      setError(updateError?.message);
    } else {
      loadPaymentMethods();
    }
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    // Logout logic here
    console.log('Logging out...');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar 
        onToggleCollapse={handleToggleSidebar}
        user={user}
        onLogout={handleLogout}
      />
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Methods</h1>
            <p className="text-gray-600">Manage your credit cards and payment information</p>
          </div>

          {/* Security Badge */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Secure Payment Processing</h3>
              <p className="text-sm text-blue-700">
                Your payment information is encrypted and securely stored. We never store your full card number or CVV.
              </p>
            </div>
            <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          </div>

          {/* Expiring Cards Warning */}
          {expiringCards?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Cards Expiring Soon</h3>
                  <p className="text-sm text-amber-700 mb-2">
                    {expiringCards?.length} payment method{expiringCards?.length > 1 ? 's' : ''} will expire within 30 days
                  </p>
                  <div className="space-y-1">
                    {expiringCards?.map(card => (
                      <div key={card?.id} className="text-sm text-amber-800">
                        {getCardBrandLogo(card?.card_brand)} ••{card?.card_last4} - 
                        Expires {card?.card_exp_month}/{card?.card_exp_year} 
                        ({card?.days_until_expiry} days remaining)
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
                ×
              </button>
            </div>
          )}

          {/* Add New Card Button */}
          {!showAddCard && (
            <Button
              onClick={() => {
                setShowAddCard(true);
                setEditingCard(null);
                resetForm();
              }}
              className="mb-6"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Card
            </Button>
          )}

          {/* Add/Edit Card Form */}
          {showAddCard && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingCard ? 'Edit Payment Method' : 'Add New Payment Method'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Card Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Card Number
                    </label>
                    <Input
                      type="text"
                      value={cardForm?.cardNumber}
                      onChange={(e) => {
                        const formatted = formatCardNumber(e?.target?.value?.replace(/[^\d]/g, '')?.slice(0, 16));
                        setCardForm({ ...cardForm, cardNumber: formatted });
                      }}
                      placeholder="1234 5678 9012 3456"
                      disabled={!!editingCard}
                      className={formErrors?.cardNumber ? 'border-red-300' : ''}
                    />
                    {formErrors?.cardNumber && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.cardNumber}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cardholder Name
                    </label>
                    <Input
                      type="text"
                      value={cardForm?.cardholderName}
                      onChange={(e) => setCardForm({ ...cardForm, cardholderName: e?.target?.value })}
                      placeholder="John Doe"
                      className={formErrors?.cardholderName ? 'border-red-300' : ''}
                    />
                    {formErrors?.cardholderName && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.cardholderName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Month
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={cardForm?.expiryMonth}
                      onChange={(e) => setCardForm({ ...cardForm, expiryMonth: e?.target?.value })}
                      placeholder="MM"
                      className={formErrors?.expiryMonth ? 'border-red-300' : ''}
                    />
                    {formErrors?.expiryMonth && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.expiryMonth}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Year
                    </label>
                    <Input
                      type="number"
                      min={new Date()?.getFullYear()}
                      value={cardForm?.expiryYear}
                      onChange={(e) => setCardForm({ ...cardForm, expiryYear: e?.target?.value })}
                      placeholder="YYYY"
                      className={formErrors?.expiryYear ? 'border-red-300' : ''}
                    />
                    {formErrors?.expiryYear && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.expiryYear}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CVV
                    </label>
                    <Input
                      type="text"
                      maxLength="4"
                      value={cardForm?.cvv}
                      onChange={(e) => setCardForm({ ...cardForm, cvv: e?.target?.value?.replace(/[^\d]/g, '') })}
                      placeholder="123"
                      disabled={!!editingCard}
                      className={formErrors?.cvv ? 'border-red-300' : ''}
                    />
                    {formErrors?.cvv && (
                      <p className="text-sm text-red-600 mt-1">{formErrors?.cvv}</p>
                    )}
                  </div>
                </div>

                {/* Billing Address */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Billing Address
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 1
                      </label>
                      <Input
                        type="text"
                        value={cardForm?.addressLine1}
                        onChange={(e) => setCardForm({ ...cardForm, addressLine1: e?.target?.value })}
                        placeholder="123 Main St"
                        className={formErrors?.addressLine1 ? 'border-red-300' : ''}
                      />
                      {formErrors?.addressLine1 && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.addressLine1}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address Line 2 (Optional)
                      </label>
                      <Input
                        type="text"
                        value={cardForm?.addressLine2}
                        onChange={(e) => setCardForm({ ...cardForm, addressLine2: e?.target?.value })}
                        placeholder="Apt 4B"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <Input
                        type="text"
                        value={cardForm?.city}
                        onChange={(e) => setCardForm({ ...cardForm, city: e?.target?.value })}
                        placeholder="New York"
                        className={formErrors?.city ? 'border-red-300' : ''}
                      />
                      {formErrors?.city && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <Input
                        type="text"
                        value={cardForm?.state}
                        onChange={(e) => setCardForm({ ...cardForm, state: e?.target?.value })}
                        placeholder="NY"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code
                      </label>
                      <Input
                        type="text"
                        value={cardForm?.postalCode}
                        onChange={(e) => setCardForm({ ...cardForm, postalCode: e?.target?.value })}
                        placeholder="10001"
                        className={formErrors?.postalCode ? 'border-red-300' : ''}
                      />
                      {formErrors?.postalCode && (
                        <p className="text-sm text-red-600 mt-1">{formErrors?.postalCode}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <select
                        value={cardForm?.country}
                        onChange={(e) => setCardForm({ ...cardForm, country: e?.target?.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 pt-4">
                  <Button type="submit">
                    {editingCard ? 'Update Card' : 'Add Card'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowAddCard(false);
                      setEditingCard(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Saved Payment Methods */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Saved Payment Methods</h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-600 mt-2">Loading payment methods...</p>
              </div>
            ) : paymentMethods?.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">No payment methods saved yet</p>
                <Button onClick={() => setShowAddCard(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Card
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paymentMethods?.map((card) => (
                  <div
                    key={card?.id}
                    className={`bg-white rounded-lg border-2 p-5 relative ${
                      card?.is_default ? 'border-blue-500' : 'border-gray-200'
                    } ${card?.is_expired ? 'opacity-60' : ''}`}
                  >
                    {/* Default Badge */}
                    {card?.is_default && (
                      <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Default
                      </div>
                    )}

                    {/* Expired/Expiring Badge */}
                    {card?.is_expired && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        Expired
                      </div>
                    )}
                    {!card?.is_expired && card?.expires_soon && (
                      <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                        Expiring Soon
                      </div>
                    )}

                    {/* Card Info */}
                    <div className="mb-4">
                      <div className="text-2xl mb-2">{getCardBrandLogo(card?.card_brand)}</div>
                      <div className="text-lg font-mono font-semibold text-gray-900">
                        •••• •••• •••• {card?.card_last4}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">{card?.billing_name}</div>
                    </div>

                    {/* Expiry */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>Expires {card?.card_exp_month}/{card?.card_exp_year}</span>
                    </div>

                    {/* Billing Address */}
                    {card?.billing_address_line1 && (
                      <div className="text-xs text-gray-500 mb-4">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {card?.billing_city}, {card?.billing_state} {card?.billing_postal_code}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      {!card?.is_default && !card?.is_expired && (
                        <button
                          onClick={() => handleSetDefault(card?.id)}
                          className="flex-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded border border-blue-600 transition-colors"
                        >
                          Set Default
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(card)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(card?.id)}
                        className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded border border-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delete Confirmation Modal */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Payment Method</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete this payment method? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleDelete(deleteConfirm)}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;